/**
 * Functionality to download files from the application to the users device or
 * computer.
 *
 * @example
 *
 * import { FileDownloader } from 'utils/files/download';
 *
 * FileDownloader.downloadBlobStream('my-document.pdf', stream).then(() => {
 *     console.log('Download was successful!');
 * }).fail(err => {
 *     console.log('Download failed:');
 *     console.log(err);
 * });
 */

import BlobStream from "blob-stream-browserify";

/**
 * External interface for the module.
 */
class FileDownloader {
  /**
   * Converts the given blob into a base64 encoded data URI that can be sent
   * as attachments in emails, opened in the browser, etc.
   *
   * Basically just a helper that wraps FileReader.readAsDataURL in a promise
   * for consistent use with the other functions of this module.
   *
   * @param blob - Blob to download
   */
  static blobAsDataURI(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
      if (!blob || !(blob instanceof Blob)) {
        reject("Expected given blob to be Blob, got: " + String(blob));
      } else {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result as string);

        reader.readAsDataURL(blob);
      }
    });
  }

  /**
   * Converts the given blob stream into a base64 encoded data URI that can
   * be sent as attachments in emails, opened in the browser, etc.
   *
   * Basically just a helper that wraps FileReader.readAsDataURL in a promise
   * for consistent use with the other functions of this module.
   *
   * @param stream - Blob to download
   * @param ctype - MIME content type
   */
  static blobStreamAsDataURI(stream: Blob, ctype: string) {
    if (!stream || !(stream instanceof BlobStream)) {
      return Promise.reject(`Expected given stream to be BlobStream, got: ${stream}`);
    }

    // @ts-ignore
    return FileDownloader.blobAsDataURI(stream.toBlob(ctype));
  }

  /**
   * Downloads given HTML5 blob.
   *
   * @param filename - Name to give downloaded file
   * @param blob - Blob to download
   */
  static downloadBlob(filename: string, blob: Blob) {
    if (!blob || !(blob instanceof Blob)) {
      throw new Error(`Expected given blob to be Blob, got: ${blob}`);
    }

    // code to download a blob as a file with a given filename, based on https://stackoverflow.com/a/19328891/953222
    const a = document.createElement("a");
    document.body.appendChild(a);
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 0);
  }

  /**
   * Downloads given NodeJS blob stream, wrapper for {@link downloadBlob} that
   * handles blob streams. See {@link downloadBlob} for implementation details.
   *
   * @param filename - Name to give downloaded file
   * @param stream - stream to download
   * @param ctype - MIME content type
   * @returns {Promise}
   */
  static downloadBlobStream(filename: string, stream: typeof BlobStream, ctype: string) {
    if (!stream || !(stream instanceof BlobStream)) {
      throw new Error(`Expected given stream to be BlobStream, got: ${stream}`);
    }

    return FileDownloader.downloadBlob(filename, stream.toBlob(ctype));
  }
}

export { FileDownloader };
